import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { ApiResponse } from '../../../../shared/dto/response/api-response.dto';
import { getApiBaseUrl } from '../../../../shared/config/runtime-config';
import { buildHttpParams } from '../../../../shared/utils/http.utils';
import {
  AccreditedByEntityPositionRequest,
  AccreditedByEntityPositionResponse,
  AccreditedByEntityPositionSummaryRequest,
  AccreditedByEntityPositionSummaryResponse,
  AccreditedByPositionRequest,
  AccreditedByPositionResponse,
  AccreditedByPositionSummaryRequest,
  AccreditedByPositionSummaryResponse,
  ActivitiesByPeriodSummaryRequest,
  ActivitiesByPeriodSummaryResponse,
  ActivitiesByStateDetailRequest,
  ActivitiesByStateDetailResponse,
  ActivitiesByStateRequest,
  ActivitiesByStateResponse,
  EntityAttendanceGapRequest,
  EntityAttendanceGapResponse,
  EntityAttendanceGapSummaryRequest,
  EntityAttendanceGapSummaryResponse,
  GuestPhotoRequest,
  GuestPhotoResponse,
  RecurrentGuestsByActivityPathRequest,
  RecurrentGuestsByActivityRequest,
  RecurrentGuestsByActivityResponse,
  ReportEventsRequest,
  ReportEventsResponse,
  ReportExportCreateRequest,
  ReportExportCreateResponse,
  ReportExportDownloadRequest,
  ReportExportDownloadResponse,
  ReportExportStatusRequest,
  ReportExportStatusResponse,
  ReportStatesResponse,
  ReportStatesRequest,
} from '../../domain/dto';
import {
  AccreditedByEntityPositionRow,
  AccreditedByEntityPositionSummary,
  AccreditedByPositionRow,
  AccreditedByPositionSummary,
  ActivitiesByPeriodSummary,
  ActivitiesByStateDetailRow,
  ActivitiesByStateRow,
  EntityAttendanceGapRow,
  EntityAttendanceGapSummary,
  RecurrentGuestRow,
  ReportExportCreateResult,
  ReportExportStatus,
  ReportPage,
} from '../../domain/model';
import { FullReportRepository } from '../../domain/repository';

@Injectable({ providedIn: 'root' })
export class FullReportRepositoryImpl implements FullReportRepository {
  constructor(private readonly http: HttpClient) {}

  recurrentGuestsByActivityPath(request: RecurrentGuestsByActivityPathRequest): string {
    const params = buildHttpParams(request);
    return `${this.reportsApiUrl}/recurrent-guests-by-activity?${params.toString()}`;
  }

  createReportExport(request: ReportExportCreateRequest): Observable<ReportExportCreateResponse> {
    const { reportType, format, filters } = request;
    return this.http
      .post<ApiResponse<Record<string, unknown>>>(
        `${this.reportsApiUrl}/${reportType}/exports`,
        {},
        {
          params: buildHttpParams({
            format,
            ...(filters ?? {}),
          }),
        },
      )
      .pipe(map((response) => this.mapReportExportCreateResult(response.data)));
  }

  getReportExportStatus(
    request: ReportExportStatusRequest,
  ): Observable<ReportExportStatusResponse> {
    const { exportId } = request;
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(`${this.reportsApiUrl}/exports/${exportId}`)
      .pipe(map((response) => this.mapReportExportStatus(response.data)));
  }

  downloadReportExport(
    request: ReportExportDownloadRequest,
  ): Observable<HttpResponse<ReportExportDownloadResponse>> {
    const { exportId } = request;
    return this.http.get(`${this.reportsApiUrl}/exports/${exportId}/download`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  getActivitiesByState(request: ActivitiesByStateRequest): Observable<ActivitiesByStateResponse> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(`${this.reportsApiUrl}/activities-by-state`, {
        params: buildHttpParams(request),
      })
      .pipe(map((response) => this.mapPage(response.data, this.mapActivitiesByStateRow)));
  }

  getActivitiesByStateDetail(
    request: ActivitiesByStateDetailRequest,
  ): Observable<ActivitiesByStateDetailResponse> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(
        `${this.reportsApiUrl}/activities-by-state-detail`,
        {
          params: buildHttpParams(request),
        },
      )
      .pipe(map((response) => this.mapPage(response.data, this.mapActivitiesByStateDetailRow)));
  }

  getActivitiesByPeriodSummary(
    request: ActivitiesByPeriodSummaryRequest,
  ): Observable<ActivitiesByPeriodSummaryResponse> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(
        `${this.reportsApiUrl}/activities-by-period-summary`,
        {
          params: buildHttpParams(request),
        },
      )
      .pipe(map((response) => this.mapActivitiesByPeriodSummary(response.data)));
  }

  getAccreditedByPositionSummary(
    request: AccreditedByPositionSummaryRequest,
  ): Observable<AccreditedByPositionSummaryResponse> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(
        `${this.reportsApiUrl}/accredited-by-position-summary`,
        {
          params: buildHttpParams(request),
        },
      )
      .pipe(map((response) => this.mapAccreditedByPositionSummary(response.data)));
  }

  getAccreditedByEntityPosition(
    request: AccreditedByEntityPositionRequest,
  ): Observable<AccreditedByEntityPositionResponse> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(
        `${this.reportsApiUrl}/accredited-by-entity-position`,
        {
          params: buildHttpParams(request),
        },
      )
      .pipe(map((response) => this.mapPage(response.data, this.mapAccreditedByEntityPositionRow)));
  }

  getAccreditedByEntityPositionSummary(
    request: AccreditedByEntityPositionSummaryRequest,
  ): Observable<AccreditedByEntityPositionSummaryResponse> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(
        `${this.reportsApiUrl}/accredited-by-entity-position-summary`,
        {
          params: buildHttpParams(request),
        },
      )
      .pipe(map((response) => this.mapAccreditedByEntityPositionSummary(response.data)));
  }

  getEntityAttendanceGap(
    request: EntityAttendanceGapRequest,
  ): Observable<EntityAttendanceGapResponse> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(`${this.reportsApiUrl}/entity-attendance-gap`, {
        params: buildHttpParams(request),
      })
      .pipe(map((response) => this.mapPage(response.data, this.mapEntityAttendanceGapRow)));
  }

  getEntityAttendanceGapSummary(
    request: EntityAttendanceGapSummaryRequest,
  ): Observable<EntityAttendanceGapSummaryResponse> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(
        `${this.reportsApiUrl}/entity-attendance-gap-summary`,
        {
          params: buildHttpParams(request),
        },
      )
      .pipe(map((response) => this.mapEntityAttendanceGapSummary(response.data)));
  }

  getAccreditedByPosition(
    request: AccreditedByPositionRequest,
  ): Observable<AccreditedByPositionResponse> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(`${this.reportsApiUrl}/accredited-by-position`, {
        params: buildHttpParams(request),
      })
      .pipe(map((response) => this.mapPage(response.data, this.mapAccreditedByPositionRow)));
  }

  getRecurrentGuestsByActivity(
    request: RecurrentGuestsByActivityRequest,
  ): Observable<RecurrentGuestsByActivityResponse> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(
        `${this.reportsApiUrl}/recurrent-guests-by-activity`,
        {
          params: buildHttpParams(request),
        },
      )
      .pipe(map((response) => this.mapPage(response.data, this.mapRecurrentGuestRow)));
  }

  getGuestPhoto(request: GuestPhotoRequest): Observable<GuestPhotoResponse> {
    const { guestId, preview } = request;
    return this.http.get(`${this.photosApiUrl}/guests/${guestId}`, {
      params: buildHttpParams({ preview }),
      responseType: 'blob',
    });
  }

  getStates(_request: ReportStatesRequest): Observable<ReportStatesResponse> {
    return this.http
      .get<ApiResponse<Array<Record<string, unknown>>>>(`${this.catalogApiUrl}/geographics/states`, {
        params: { countryId: environment.COUNTRY_ID },
      })
      .pipe(
        map((response) =>
          (response.data ?? []).map((item) => ({
            id: this.toNumber(item['id']),
            name: this.toString(item['name']),
          })),
        ),
      );
  }

  getEvents(request?: ReportEventsRequest): Observable<ReportEventsResponse> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(this.eventsApiUrl, {
        params: buildHttpParams(request ?? { page: 1, pageSize: 100 }),
      })
      .pipe(
        map((response) => {
          const items = Array.isArray(response.data?.['items']) ? response.data['items'] : [];
          return items.map((item) => {
            const row = this.toRecord(item);
            return {
              id: this.toNumber(row['id']),
              code: this.toString(row['code']),
              name: this.toString(row['name']),
              description: this.toString(row['description']),
              created: this.toNullableString(row['created']),
            };
          });
        }),
      );
  }

  private mapPage<T>(
    data: Record<string, unknown> | undefined,
    mapper: (input: Record<string, unknown>) => T,
  ): ReportPage<T> {
    const itemsRaw = Array.isArray(data?.['items']) ? data?.['items'] : [];

    return {
      items: itemsRaw.map((item) => mapper(this.toRecord(item))),
      page: this.toNumber(data?.['page']),
      pageSize: this.toNumber(data?.['pageSize']),
      totalRecords: this.toNumber(data?.['totalRecords']),
    };
  }

  private mapReportExportCreateResult = (
    item: Record<string, unknown> | undefined,
  ): ReportExportCreateResult => {
    const row = this.toRecord(item);
    return {
      exportId: this.toNumber(row['exportId']),
      status: this.toString(row['status']),
      requestedAt: this.toString(row['requestedAt']),
    };
  };

  private mapReportExportStatus = (
    item: Record<string, unknown> | undefined,
  ): ReportExportStatus => {
    const row = this.toRecord(item);
    return {
      exportId: this.toNumber(row['exportId']),
      reportType: this.toString(row['reportType']),
      format: this.toString(row['format']),
      status: this.toString(row['status']),
      progress: this.toNumber(row['progress']),
      requestedAt: this.toString(row['requestedAt']),
      startedAt: this.toNullableString(row['startedAt']),
      finishedAt: this.toNullableString(row['finishedAt']),
      expiresAt: this.toNullableString(row['expiresAt']),
      errorCode: this.toNullableString(row['errorCode']),
      errorMessage: this.toNullableString(row['errorMessage']),
    };
  };

  private mapActivitiesByStateRow = (item: Record<string, unknown>): ActivitiesByStateRow => ({
    stateId: this.toNumber(item['stateId']),
    stateName: this.toString(item['stateName']),
    totalActivities: this.toNumber(item['totalActivities']),
  });

  private mapActivitiesByStateDetailRow = (
    item: Record<string, unknown>,
  ): ActivitiesByStateDetailRow => ({
    eventId: this.toNumber(item['eventId']),
    eventCode: this.toString(item['eventCode']),
    eventName: this.toString(item['eventName']),
    activityDate: this.toString(item['activityDate']),
  });

  private mapActivitiesByPeriodSummary = (
    item: Record<string, unknown> | undefined,
  ): ActivitiesByPeriodSummary => {
    const row = this.toRecord(item);
    return {
      periodLabel: this.toString(row['periodLabel']),
      totalActivities: this.toNumber(row['totalActivities']),
    };
  };

  private mapAccreditedByPositionSummary = (
    item: Record<string, unknown> | undefined,
  ): AccreditedByPositionSummary => {
    const row = this.toRecord(item);
    return {
      positionLabel: this.toString(row['positionLabel']),
      totalAccredited: this.toNumber(row['totalAccredited']),
    };
  };

  private mapAccreditedByEntityPositionRow = (
    item: Record<string, unknown>,
  ): AccreditedByEntityPositionRow => ({
    eventId: this.toNumber(item['eventId']),
    eventCode: this.toString(item['eventCode']),
    eventName: this.toString(item['eventName']),
    totalAccredited: this.toNumber(item['totalAccredited']),
  });

  private mapAccreditedByEntityPositionSummary = (
    item: Record<string, unknown> | undefined,
  ): AccreditedByEntityPositionSummary => {
    const row = this.toRecord(item);
    return {
      entityLabel: this.toString(row['entityLabel']),
      positionLabel: this.toString(row['positionLabel']),
      totalAccredited: this.toNumber(row['totalAccredited']),
    };
  };

  private mapEntityAttendanceGapRow = (item: Record<string, unknown>): EntityAttendanceGapRow => ({
    eventId: this.toNumber(item['eventId']),
    eventCode: this.toString(item['eventCode']),
    eventName: this.toString(item['eventName']),
    normalizedEntity: this.toString(item['normalizedEntity']),
    displayEntity: this.toString(item['displayEntity']),
    totalConvoked: this.toNumber(item['totalConvoked']),
    totalAttended: this.toNumber(item['totalAttended']),
    attendanceGap: this.toNumber(item['attendanceGap']),
    attendanceGapPercent: this.toNumber(item['attendanceGapPercent']),
    participationPercent: this.toNumber(item['participationPercent']),
  });

  private mapEntityAttendanceGapSummary = (
    item: Record<string, unknown> | undefined,
  ): EntityAttendanceGapSummary => {
    const row = this.toRecord(item);
    return {
      eventId: this.toNumber(row['eventId']),
      eventCode: this.toString(row['eventCode']),
      eventName: this.toString(row['eventName']),
      entityLabel: this.toString(row['entityLabel']),
      totalConvoked: this.toNumber(row['totalConvoked']),
      totalAttended: this.toNumber(row['totalAttended']),
      attendanceGap: this.toNumber(row['attendanceGap']),
      attendanceGapPercent: this.toNumber(row['attendanceGapPercent']),
      participationPercent: this.toNumber(row['participationPercent']),
    };
  };

  private mapAccreditedByPositionRow = (
    item: Record<string, unknown>,
  ): AccreditedByPositionRow => ({
    normalizedPosition: this.toString(item['normalizedPosition']),
    displayPosition: this.toString(item['displayPosition']),
    totalAccredited: this.toNumber(item['totalAccredited']),
  });

  private mapRecurrentGuestRow = (item: Record<string, unknown>): RecurrentGuestRow => ({
    guestId: this.toNumber(item['guestId']),
    fullIdentification: this.toString(item['fullIdentification']),
    name: this.toString(item['name']),
    lastName: this.toString(item['lastName']),
    birthDate: this.toNullableString(item['birthDate']),
    entity: this.toString(item['entity']),
    position: this.toString(item['position']),
    totalActivities: this.toNumber(item['totalActivities']),
    lastActivityDate: this.toNullableString(item['lastActivityDate']),
    photoPreviewContentType: this.toNullableString(item['photoPreviewContentType']),
    photoPreviewBlob: this.toBlobValue(item['photoPreviewBlob']),
  });

  private toBlobValue(value: unknown): string | number[] | null {
    if (typeof value === 'string') {
      return value;
    }
    if (Array.isArray(value)) {
      return value.filter((item): item is number => Number.isFinite(Number(item))).map(Number);
    }
    return null;
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private toNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private toString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  private toNullableString(value: unknown): string | null {
    const parsed = this.toString(value).trim();
    return parsed.length > 0 ? parsed : null;
  }

  private get reportsApiUrl(): string {
    return getApiBaseUrl('reports');
  }

  private get photosApiUrl(): string {
    return getApiBaseUrl('photos');
  }

  private get eventsApiUrl(): string {
    return getApiBaseUrl('events');
  }

  private get catalogApiUrl(): string {
    return getApiBaseUrl('catalogs');
  }
}
